// ────────────────────────────────────────────────────────────────────
// PhotoMarkup — flagship-chat §D: a corrosion photo attached to a delivery
// renders as an inline thumbnail (with any saved markup already drawn on
// top) and can be opened into a small annotator to point at a defect —
// circle / arrow / text label, in a fixed small color set.
//
// Shapes are stored as a JSON array with NORMALIZED 0..1 coordinates (see
// server/routers/intake.py PATCH /{file_id}/annotation) so the SAME overlay
// renders correctly at thumbnail size and full-size alike — the overlay <svg>
// uses percentage units (no viewBox scaling trick needed), which map 0..1
// straight to "0%..100%" of whatever box it's drawn over.
//
// Bounded MVP: 3 tools, one undo, save/cancel. No layers, no resize, no
// freehand — see docs/superpowers/specs/2026-07-21-flagship-chat.md §D.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Circle, ArrowUpRight, Type, Undo2, Users, Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import { apiClient, fetchIntakeFileBlob } from "@/lib/api";
import type { MarkupShape } from "@/lib/types";
import { connectMarkupLive, genLiveId, type MarkupLiveHandle, type RosterPeer } from "@/lib/markupLive";
import { useVoiceCall, type CallStatus } from "@/lib/voiceCall";
import { FileChip } from "./TeamChatDock";
import { RevBadge } from "./RevBadge";

// Stand-in MarkupLiveHandle for the render(s) before connectMarkupLive's
// effect has actually opened a socket (see MarkupModal below) — every
// method is a safe no-op, same posture as markupLive.ts's own internal
// `noop` handle for "no auth token yet". useVoiceCall degrades the same way
// PhotoMarkup already degrades without live markup: nothing to call yet,
// no exception, no special-cased branch at the call site.
const NOOP_LIVE_HANDLE: MarkupLiveHandle = {
  sendCursor: () => {}, sendShapeAdd: () => {}, sendShapeRemove: () => {},
  sendCallOffer: () => {}, sendCallAnswer: () => {}, sendCallIce: () => {}, sendCallEnd: () => {},
  close: () => {},
};

/** Thai copy for each honest call state — see voiceCall.ts's module
 *  docstring for why `ice-failed` exists as its own state (STUN-only, no
 *  TURN server in this codebase; symmetric-NAT/corporate networks fail to
 *  connect and this is that failure surfaced, not a hang). */
function callStatusLabel(status: CallStatus): string | null {
  switch (status) {
    case "requesting-mic": return "กำลังขอสิทธิ์ไมโครโฟน…";
    case "mic-denied": return "ไม่ได้รับอนุญาตให้ใช้ไมโครโฟน";
    case "ringing-outgoing": return "กำลังโทร…";
    case "connecting": return "กำลังเชื่อมต่อ…";
    case "connected": return "สายเชื่อมต่อแล้ว";
    case "ice-failed": return "เชื่อมต่อไม่ได้ — เครือข่ายอาจต้องใช้ TURN";
    case "busy": return "อีกฝ่ายกำลังคุยสายอยู่";
    case "ended": return "สายสิ้นสุดแล้ว";
    default: return null;
  }
}

// The minimal shape PhotoThumb/MarkupModal need — a structural subset of
// IntakeFile, same convention as FileChip's own minimal {id, filename} shape,
// so callers with only a partial file object (as tests often build) still fit.
export interface PhotoFile {
  id: number;
  filename: string;
  kind?: string;
  annotation?: string | null;
  revision?: string | null;
  is_current?: boolean;
}

const PALETTE = ["#C0392B", "#F49019", "#F1C40F", "#1F2937"] as const;

/** Defensive parse: a malformed/foreign annotation degrades to "no markup",
 *  never throws. Anything that isn't an array of {type: circle|arrow|text}
 *  dicts is dropped shape-by-shape rather than discarding the whole array —
 *  one bad shape shouldn't hide the rest. */
export function parseShapes(annotation: string | null | undefined): MarkupShape[] {
  if (!annotation) return [];
  try {
    const data: unknown = JSON.parse(annotation);
    if (!Array.isArray(data)) return [];
    return data.filter((s): s is MarkupShape =>
      !!s && typeof s === "object" && ["circle", "arrow", "text"].includes((s as { type?: unknown }).type as string));
  } catch {
    return [];
  }
}

function markerId(color: string): string {
  return `markup-arrowhead-${color.replace("#", "")}`;
}

/** The read-only SVG shape layer — used both on the inline thumbnail and
 *  inside the editor (as the "committed shapes" base under the live draft).
 *
 *  `peerColors` (live co-markup only — always undefined on the thumbnail's
 *  read-only render) is an array parallel to `shapes`: a non-null entry
 *  marks a shape that arrived from a PEER during this open editing session,
 *  not one the local user drew or one already persisted. Those get a small
 *  colored dot badge at the shape's anchor point in the peer's color — "their
 *  strokes are visually distinct until save" (see PhotoMarkup.tsx's
 *  MarkupModal / markupLive.ts). The badge is cosmetic only: the shape
 *  itself still renders in whatever color its author picked, and the badge
 *  never reaches the persisted annotation (peerColors is never part of the
 *  saved shape objects). */
export function MarkupOverlay({ shapes, peerColors }:
  { shapes: MarkupShape[]; peerColors?: (string | null)[] }): JSX.Element {
  const arrowColors = Array.from(new Set(shapes.filter((s) => s.type === "arrow").map((s) => s.color)));
  const anchor = (s: MarkupShape): { x: number; y: number } =>
    s.type === "circle" ? { x: s.cx, y: s.cy } : s.type === "arrow" ? { x: s.x1, y: s.y1 } : { x: s.x, y: s.y };
  return (
    <svg data-testid="markup-overlay" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
      {arrowColors.length > 0 && (
        <defs>
          {arrowColors.map((c) => (
            <marker key={c} id={markerId(c)} viewBox="0 0 10 10" refX="8" refY="5"
                    markerWidth={5} markerHeight={5} orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 Z" fill={c} />
            </marker>
          ))}
        </defs>
      )}
      {shapes.map((s, i) => {
        if (s.type === "circle") {
          return (
            <circle key={i} cx={`${s.cx * 100}%`} cy={`${s.cy * 100}%`} r={`${Math.max(s.r, 0.01) * 100}%`}
                    fill="none" stroke={s.color} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
          );
        }
        if (s.type === "arrow") {
          return (
            <line key={i} x1={`${s.x1 * 100}%`} y1={`${s.y1 * 100}%`} x2={`${s.x2 * 100}%`} y2={`${s.y2 * 100}%`}
                  stroke={s.color} strokeWidth={2.5} vectorEffect="non-scaling-stroke"
                  markerEnd={`url(#${markerId(s.color)})`} />
          );
        }
        return (
          <text key={i} x={`${s.x * 100}%`} y={`${s.y * 100}%`} fill={s.color}
                fontFamily="ui-monospace, monospace" fontSize={12} fontWeight={700}
                stroke="var(--paper)" strokeWidth={3} paintOrder="stroke" strokeLinejoin="round">
            {s.text}
          </text>
        );
      })}
      {peerColors?.map((color, i) => {
        if (!color) return null;
        const a = anchor(shapes[i]);
        return (
          <circle key={`peer-${i}`} data-testid="peer-shape-badge" cx={`${a.x * 100}%`} cy={`${a.y * 100}%`}
                  r={4} fill={color} stroke="var(--paper)" strokeWidth={1.5} />
        );
      })}
    </svg>
  );
}

// ── Inline thumbnail ──────────────────────────────────────────────────
// Fetches the file's bytes via the authed download route (no <img src> —
// the route is Bearer-gated) and shows it as a small clickable image with
// its saved markup overlaid. Loading and error states BOTH degrade to the
// plain FileChip — a photo that fails to preview is still a downloadable
// file, never a dead end.
export function PhotoThumb({ file, deliveryId, showSuperseded }: {
  file: PhotoFile; deliveryId: number;
  /** Show the muted "ถูกแทนที่" marker when superseded — staff contexts only,
   *  see RevBadge's docstring. */
  showSuperseded?: boolean;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    fetchIntakeFileBlob(file.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (failed || !url) return <FileChip file={file} showSuperseded={showSuperseded} />;

  const shapes = parseShapes(file.annotation);
  const revTitle = file.revision ? `${file.filename} · Rev.${file.revision}` : file.filename;
  return (
    <>
      <button type="button" onClick={() => setEditing(true)} title={revTitle}
              className="relative block overflow-hidden"
              style={{ width: 96, height: 72, border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
        <img src={url} alt={file.filename} className="w-full h-full object-cover" />
        <MarkupOverlay shapes={shapes} />
        {file.revision && (
          <span className="absolute bottom-1 left-1">
            <RevBadge revision={file.revision} isCurrent={file.is_current} showSuperseded={showSuperseded} />
          </span>
        )}
      </button>
      {editing && (
        <MarkupModal file={file} imgUrl={url} deliveryId={deliveryId} onClose={() => setEditing(false)} />
      )}
    </>
  );
}

// ── Markup editor ─────────────────────────────────────────────────────
type Tool = "circle" | "arrow" | "text";
interface Draft { x0: number; y0: number; x1: number; y1: number }

/** Pointer position as a 0..1 fraction of `el`'s rendered box. Clamped to
 *  [0,1] (a drag that leaves the image still records a shape at the edge,
 *  not an off-canvas one); null on a zero-size box (can't happen with a
 *  loaded <img>, but jsdom-in-tests has no real layout — guards against
 *  NaN coordinates ever reaching state). */
function relPos(clientX: number, clientY: number, el: HTMLElement): { x: number; y: number } | null {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: Math.min(Math.max(x, 0), 1), y: Math.min(Math.max(y, 0), 1) };
}

const MIN_DRAG = 0.005; // ~a few px on a typical thumbnail — filters an accidental click-as-drag

function ToolButton({ Icon, label, active, onClick }:
  { Icon: typeof Circle; label: string; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
            className="flex items-center gap-1 px-2 py-1 font-mono uppercase"
            style={{ fontSize: 9.5, fontWeight: active ? 700 : 400,
                     color: active ? "var(--accent-deep)" : "var(--ink-3)",
                     background: active ? "rgba(var(--accent-rgb) / 0.10)" : "transparent",
                     border: `1px solid ${active ? "var(--accent)" : "transparent"}` }}>
      <Icon size={13} />
      {label}
    </button>
  );
}

// Throttle interval for outgoing cursor broadcasts — frequent enough to read
// as a live pointer, cheap enough not to spam the relay (see markup_ws.py's
// own 40/sec-per-connection cap, well above what an 80ms throttle can hit).
const CURSOR_THROTTLE_MS = 80;

/** One committed shape plus its live-co-markup provenance. `shape` is exactly
 *  what gets persisted (see saveMut below — `entries.map(e => e.shape)`
 *  strips this wrapper entirely, so the saved annotation schema is
 *  byte-for-byte what it always was). `peerColor` is non-null only for a
 *  shape that arrived from ANOTHER open editor during this session — used to
 *  render the peer badge (MarkupOverlay's peerColors) and, deliberately,
 *  NEVER re-broadcast if undone (see `undo` below: only the local user's own
 *  live-authored shapes get a shape_remove sent). `liveId` is set for any
 *  shape added THROUGH the live channel this session, whether authored
 *  locally or received from a peer; it's what makes an incoming
 *  `shape_remove` unambiguous (matched by id, never by array position). */
interface ShapeEntry {
  shape: MarkupShape;
  peerColor: string | null;
  liveId: string | null;
}

interface RemoteCursor extends RosterPeer {
  x: number;
  y: number;
}

export function MarkupModal({ file, imgUrl, deliveryId, onClose }:
  { file: PhotoFile; imgUrl: string; deliveryId: number; onClose: () => void }): JSX.Element {
  const [entries, setEntries] = useState<ShapeEntry[]>(
    () => parseShapes(file.annotation).map((shape) => ({ shape, peerColor: null, liveId: null })));
  const [tool, setTool] = useState<Tool>("circle");
  const [color, setColor] = useState<string>(PALETTE[0]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [textPrompt, setTextPrompt] = useState<{ x: number; y: number } | null>(null);
  const [textValue, setTextValue] = useState("");
  // Peers currently viewing (roster + join/leave) and their live cursor
  // positions — separate state because a peer can be present with no cursor
  // reported yet (roster/join arrive before their first cursor move).
  const [peers, setPeers] = useState<Record<string, RosterPeer>>({});
  const [cursors, setCursors] = useState<Record<string, RemoteCursor>>({});
  const draggingRef = useRef(false);
  const liveRef = useRef<ReturnType<typeof connectMarkupLive> | null>(null);
  const lastCursorSentRef = useRef(0);
  const queryClient = useQueryClient();

  // The markup live connection isn't open yet on first render (it's opened
  // in the effect below); until it is, voice call sends degrade through
  // NOOP_LIVE_HANDLE exactly the way markupLive.ts's own handle degrades
  // with no auth token — see useVoiceCall's docstring on why its handlers
  // stay safe to wire in even before this state updates.
  const [liveHandle, setLiveHandle] = useState<MarkupLiveHandle | null>(null);
  const voice = useVoiceCall(file.id, liveHandle ?? NOOP_LIVE_HANDLE);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  const shapes = useMemo(() => entries.map((e) => e.shape), [entries]);
  const peerColors = useMemo(() => entries.map((e) => e.peerColor), [entries]);

  // Connect once per open modal (keyed on file.id — this modal never
  // switches files without unmounting), close on unmount. A WS that never
  // connects (offline, server down) or drops twice just means these
  // handlers stop firing — see markupLive.ts's module docstring; the editor
  // itself never branches on live-connection state. Voice call signaling
  // (roadmap D3) piggybacks on this SAME connection — see markupLive.ts's
  // and markup_ws.py's module docstrings for why — so `voice.handlers` is
  // spread straight into the same handlers object rather than opening a
  // second socket.
  useEffect(() => {
    const live = connectMarkupLive(file.id, {
      onRoster: (roster) => {
        setPeers(Object.fromEntries(roster.map((p) => [p.connId, p])));
      },
      onJoin: (peer) => {
        setPeers((p) => ({ ...p, [peer.connId]: peer }));
      },
      onLeave: (connId) => {
        setPeers((p) => { const next = { ...p }; delete next[connId]; return next; });
        setCursors((c) => { const next = { ...c }; delete next[connId]; return next; });
      },
      onCursor: (peer, x, y) => {
        setCursors((c) => ({ ...c, [peer.connId]: { ...peer, x, y } }));
      },
      onShapeAdd: (peer, shape, liveId) => {
        if (!shape || typeof shape !== "object") return;
        if (!["circle", "arrow", "text"].includes((shape as { type?: unknown }).type as string)) return;
        setEntries((es) => [...es, { shape, peerColor: peer.color, liveId }]);
      },
      onShapeRemove: (liveId) => {
        setEntries((es) => es.filter((e) => e.liveId !== liveId));
      },
      ...voice.handlers,
    });
    liveRef.current = live;
    setLiveHandle(live);
    return () => {
      live.close();
      setLiveHandle(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  // Attach the remote party's audio the moment a track arrives — a plain
  // DOM assignment (not `src`) because MediaStream isn't a URL; there is no
  // JSX prop for srcObject. Runs again (clearing to null) on hangup/ended
  // so nothing keeps playing from a MediaStream whose tracks were already
  // stopped by voiceCall.ts's teardown().
  useEffect(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = voice.remoteStream;
  }, [voice.remoteStream]);

  const saveMut = useMutation({
    mutationFn: () => apiClient.saveAnnotation(file.id, shapes.length ? JSON.stringify(shapes) : null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["submission-files", deliveryId] });
      onClose();
    },
  });

  const handleDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (tool === "text") return;               // text commits on click, not drag
    const pos = relPos(e.clientX, e.clientY, e.currentTarget);
    if (!pos) return;
    draggingRef.current = true;
    setDraft({ x0: pos.x, y0: pos.y, x1: pos.x, y1: pos.y });
  };
  const handleMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const pos = relPos(e.clientX, e.clientY, e.currentTarget);
    // Broadcast the local cursor on ANY move over the canvas — not just
    // while drawing — so a peer sees the pointer travelling, throttled to
    // CURSOR_THROTTLE_MS regardless of what's happening below.
    if (pos) {
      // Date.now(), not performance.now(): the ~80ms granularity doesn't need
      // high-resolution timing, and Date.now() is what vi.useFakeTimers() /
      // vi.setSystemTime() control in tests — see PhotoMarkup.test.tsx.
      const now = Date.now();
      if (now - lastCursorSentRef.current >= CURSOR_THROTTLE_MS) {
        lastCursorSentRef.current = now;
        liveRef.current?.sendCursor(pos.x, pos.y);
      }
    }
    if (!draggingRef.current) return;
    if (!pos) return;
    setDraft((d) => (d ? { ...d, x1: pos.x, y1: pos.y } : d));
  };
  const handleUp = (): void => {
    draggingRef.current = false;
    setDraft((d) => {
      if (!d) return null;
      const dist = Math.hypot(d.x1 - d.x0, d.y1 - d.y0);
      if (dist >= MIN_DRAG) {
        const liveId = genLiveId();
        let shape: MarkupShape | null = null;
        if (tool === "circle") shape = { type: "circle", cx: d.x0, cy: d.y0, r: dist, color };
        else if (tool === "arrow") shape = { type: "arrow", x1: d.x0, y1: d.y0, x2: d.x1, y2: d.y1, color };
        if (shape) {
          setEntries((es) => [...es, { shape, peerColor: null, liveId }]);
          liveRef.current?.sendShapeAdd(shape, liveId);
        }
      }
      return null;
    });
  };
  const handleClick = (e: ReactMouseEvent<HTMLDivElement>): void => {
    if (tool !== "text") return;
    const pos = relPos(e.clientX, e.clientY, e.currentTarget);
    if (!pos) return;
    setTextPrompt(pos);
    setTextValue("");
  };
  const commitText = (): void => {
    const t = textValue.trim();
    if (t && textPrompt) {
      const liveId = genLiveId();
      const shape: MarkupShape = { type: "text", x: textPrompt.x, y: textPrompt.y, text: t.slice(0, 60), color };
      setEntries((es) => [...es, { shape, peerColor: null, liveId }]);
      liveRef.current?.sendShapeAdd(shape, liveId);
    }
    setTextPrompt(null);
    setTextValue("");
  };
  const undo = (): void => {
    setEntries((es) => {
      if (es.length === 0) return es;
      const last = es[es.length - 1];
      // Only relay the removal of a shape THIS user authored live — undoing
      // a peer's shape merely hides it from this local view (it's still on
      // their canvas; nothing to tell them), matching the bounded-MVP "one
      // undo" scope this editor has always had.
      if (last.peerColor === null && last.liveId) liveRef.current?.sendShapeRemove(last.liveId);
      return es.slice(0, -1);
    });
  };

  const peerList = Object.values(peers);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={onClose} role="dialog" aria-modal="true" aria-label="แก้ไขภาพประกอบ"
         className="fixed inset-0 z-[3000] flex items-center justify-center"
         style={{ background: "rgba(8,12,18,.88)", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-2" style={{ maxWidth: "94vw" }}>
        <div className="flex items-center gap-1.5 px-2 py-1.5 flex-wrap"
             style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
          <ToolButton Icon={Circle} label="วงกลม" active={tool === "circle"} onClick={() => setTool("circle")} />
          <ToolButton Icon={ArrowUpRight} label="ลูกศร" active={tool === "arrow"} onClick={() => setTool("arrow")} />
          <ToolButton Icon={Type} label="ข้อความ" active={tool === "text"} onClick={() => setTool("text")} />
          <span style={{ width: 1, height: 20, background: "var(--hairline)" }} />
          {PALETTE.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} aria-label={`สี ${c}`} aria-pressed={color === c}
                    style={{ width: 15, height: 15, background: c, borderRadius: 2,
                             border: `2px solid ${color === c ? "var(--ink-1)" : "var(--hairline)"}` }} />
          ))}
          <span style={{ width: 1, height: 20, background: "var(--hairline)" }} />
          <button type="button" onClick={undo} disabled={shapes.length === 0}
                  className="flex items-center gap-1 px-1.5 py-1 font-mono uppercase disabled:opacity-40"
                  style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
            <Undo2 size={12} /> เลิกทำ
          </button>
          <span style={{ width: 1, height: 20, background: "var(--hairline)" }} />

          {/* Voice 1:1 (roadmap D3) — a call is 1:1 P2P only (see
              voiceCall.ts's module docstring for the STUN-only / no-TURN
              honesty note); the button offers a call to "whoever else is
              in the room", which for this bounded room-of-≤8 feature is
              normally the one other person doing the co-markup. */}
          {(voice.status === "idle" || voice.status === "ended" || voice.status === "busy") && (
            <button type="button" onClick={voice.startCall} disabled={peerList.length === 0}
                    title={peerList.length === 0 ? "ยังไม่มีใครอยู่ร่วมกันในหน้านี้" : "โทรหาผู้ที่กำลังดูร่วมกัน"}
                    className="flex items-center gap-1 px-1.5 py-1 font-mono uppercase disabled:opacity-40"
                    style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
              <Phone size={12} /> โทร
            </button>
          )}
          {voice.status !== "idle" && voice.status !== "ended" && voice.status !== "ringing-incoming" && (
            <div data-testid="call-status-pill" className="flex items-center gap-1.5 px-1.5 py-1 font-mono"
                 style={{ fontSize: 9.5, color: voice.status === "ice-failed" ? "var(--err)" : "var(--ink-2)" }}>
              <span>{callStatusLabel(voice.status)}{voice.peerName ? ` — ${voice.peerName}` : ""}</span>
              {(voice.status === "connected" || voice.status === "connecting" || voice.status === "ringing-outgoing") && (
                <>
                  <button type="button" onClick={voice.toggleMute} aria-pressed={voice.muted}
                          aria-label={voice.muted ? "เปิดไมโครโฟน" : "ปิดไมโครโฟน"}
                          style={{ color: voice.muted ? "var(--err)" : "var(--ink-3)" }}>
                    {voice.muted ? <MicOff size={13} /> : <Mic size={13} />}
                  </button>
                  <button type="button" onClick={voice.hangUp} aria-label="วางสาย" style={{ color: "var(--err)" }}>
                    <PhoneOff size={13} />
                  </button>
                </>
              )}
            </div>
          )}
          <span className="flex-1" />
          {saveMut.isError && (
            <span className="font-mono" style={{ fontSize: 9.5, color: "var(--err)" }}>บันทึกไม่สำเร็จ</span>
          )}
          <button type="button" onClick={onClose}
                  className="px-2 py-1 font-mono uppercase" style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
            ยกเลิก
          </button>
          <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
                  className="px-2.5 py-1 font-mono uppercase disabled:opacity-40"
                  style={{ fontSize: 9.5, fontWeight: 700, color: "#fff", background: "var(--accent)" }}>
            {saveMut.isPending ? "กำลังบันทึก…" : "บันทึก"}
          </button>
        </div>

        {peerList.length > 0 && (
          <div data-testid="co-viewing-line" className="flex items-center gap-1.5 px-2 py-1 font-mono"
               style={{ fontSize: 10, color: "var(--ink-2)", background: "var(--paper)",
                        border: "1px solid var(--hairline)" }}>
            <Users size={11} style={{ color: "var(--ok)" }} />
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--ok)", flexShrink: 0 }} />
            กำลังดูร่วมกัน: {peerList.map((p) => p.name).join(", ")}
          </div>
        )}

        {voice.status === "ringing-incoming" && (
          <div data-testid="incoming-call-prompt" className="flex items-center gap-2 px-2 py-1.5 font-mono"
               style={{ fontSize: 10.5, color: "var(--ink-1)", background: "var(--paper)",
                        border: "1px solid var(--accent)" }}>
            <Phone size={13} style={{ color: "var(--accent)" }} />
            <span className="flex-1">{voice.peerName ?? "มีสาย"}กำลังโทรเข้า</span>
            <button type="button" onClick={voice.acceptCall}
                    className="px-2 py-1 font-mono uppercase" style={{ fontSize: 9.5, fontWeight: 700, color: "#fff", background: "var(--ok)" }}>
              รับสาย
            </button>
            <button type="button" onClick={voice.rejectCall}
                    className="px-2 py-1 font-mono uppercase" style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
              ปฏิเสธ
            </button>
          </div>
        )}

        {/* Remote party's audio — no visible player (playback controls live
            in the call-status pill above), just the element WebRTC's
            ontrack attaches a MediaStream to. autoPlay + playsInline: a
            call's whole point is "hands stay on the photo", not clicking a
            play button on an audio tag. */}
        <audio ref={remoteAudioRef} autoPlay playsInline hidden data-testid="remote-call-audio" />

        <div className="relative inline-block" style={{ cursor: tool === "text" ? "text" : "crosshair", lineHeight: 0 }}
             onPointerDown={handleDown} onPointerMove={handleMove} onPointerUp={handleUp} onClick={handleClick}>
          <img src={imgUrl} alt={file.filename} draggable={false}
               style={{ maxWidth: "88vw", maxHeight: "76vh", objectFit: "contain",
                        border: "1px solid rgba(255,255,255,.14)", display: "block" }} />
          <MarkupOverlay shapes={shapes} peerColors={peerColors} />
          {Object.values(cursors).map((c) => (
            <div key={c.connId} data-testid="remote-cursor" className="absolute pointer-events-none"
                 style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, transform: "translate(-2px, -2px)", zIndex: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color,
                            border: "1.5px solid var(--paper)" }} />
              <span className="font-mono whitespace-nowrap"
                    style={{ fontSize: 10, color: "#fff", background: c.color, padding: "1px 4px",
                             borderRadius: 2, marginLeft: 4, display: "inline-block", transform: "translateY(-14px)" }}>
                {c.name}
              </span>
            </div>
          ))}
          {draft && (tool === "circle" || tool === "arrow") && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
              {tool === "circle" ? (
                <circle cx={`${draft.x0 * 100}%`} cy={`${draft.y0 * 100}%`}
                        r={`${Math.hypot(draft.x1 - draft.x0, draft.y1 - draft.y0) * 100}%`}
                        fill="none" stroke={color} strokeDasharray="4 3" strokeWidth={2} />
              ) : (
                <line x1={`${draft.x0 * 100}%`} y1={`${draft.y0 * 100}%`} x2={`${draft.x1 * 100}%`} y2={`${draft.y1 * 100}%`}
                      stroke={color} strokeDasharray="4 3" strokeWidth={2} />
              )}
            </svg>
          )}
          {textPrompt && (
            <div className="absolute" style={{ left: `${textPrompt.x * 100}%`, top: `${textPrompt.y * 100}%` }}>
              <input autoFocus value={textValue} onChange={(e) => setTextValue(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") commitText();
                       if (e.key === "Escape") { setTextPrompt(null); setTextValue(""); }
                     }}
                     onBlur={commitText} placeholder="ป้ายกำกับ…" maxLength={60}
                     className="font-mono px-1 py-0.5" style={{ fontSize: 11, width: 140,
                              border: `1px solid ${color}`, background: "var(--paper)", color: "var(--ink-1)" }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
